import type { WriteTransaction } from 'replicache'
import {
  isBlockWithChildren,
  isComponentBlock,
  isInstanceBlock,
  isDocumentBlock,
  isMediaBlock,
  normalizeStringPath,
  isEventBlock,
  isListBlock,
  isRichTextBlock,
  getVariantsDataFromKey,
  getVariantsKey,
} from '@basehub/data/replicache/index'
import type {
  Block,
  BlockType,
  BlockWithPath,
  BlockWithChildren,
  ListBlock,
  BlockRelationship,
  CommonBlocksCompoundIndexes,
  RichTextBlock,
  EventBlock,
  VariantData,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { getIdempotentId } from '@basehub/utils/id/index'
import { validateBlockNestingPromise } from '../helpers/component-helpers'
import { updateRichTextStateBasedOnNewChildren } from './rich-text'
import { type ReorderBlockParams, reorderBlocks } from './reorder'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { WithServerSecret } from '@basehub/data/replicache/tx/spec'
import cloneDeep from 'lodash/cloneDeep'
import { areBlocksCompatible } from '@basehub/data/replicache/schema/block/compare'
import { doCopyOnWrite, idToSourceBlockId } from './copy-on-write'
import { TreeManager } from '@basehub/utils/tree/index'
import {
  createBlock,
  deleteBlock,
  updateBlock,
  type AtLeast,
  type DuplicateBlockParams,
  getBlocks,
} from './crud'
import { BlockBatchMutationParams } from './batch'
import {
  BaseHubTransaction,
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { hasAccessOrThrow } from '@basehub/data/replicache/mutators/team-space/team/auth'
import { editorCanEditBlocks } from '@basehub/data/replicache/schema/block/permissions'
import {
  createIndexedMap,
  type IndexedMap,
} from '@basehub/utils/indexed-map/index'

/* -------------------------------------------------------------------------------------------------
 * PASTE
 * -----------------------------------------------------------------------------------------------*/

type PasteCopyParams = Omit<
  DuplicateBlockParams,
  'duplicatedBlockId' | 'newBlockId'
> & {
  pastedBlocksData: [BlockWithPath, ...BlockWithPath[]]
  supportBlocksData: Block[]
  rootId: string
  type: 'copy'
}

export type PasteBlockParams = {
  replaceBlockId?: Block['id']
  selectedBlock?: {
    id: string
    path: string
    type: Block['type']
    instanceChildTarget: string | undefined | null
  }
  shiftKeyPressed: boolean
  repoId: string
  replaceValueOnly: boolean
} & ((ReorderBlockParams & { type: 'cut'; path: string }) | PasteCopyParams)

function saveBlocksToCreate(
  _tx: BaseHubTransaction,
  params: {
    blocks: BlockWithPath[]
    idSeed: string
    repoId: string
    supportBlocksData: Block[]
    treeManager: TreeManager<Block, CommonBlocksCompoundIndexes>
    path: string
    parentBlock: Block
  }
): {
  blocksToCreate: {
    block: Block
    path: string
    isNested: boolean
  }[]
  blocksToCreateMap: IndexedMap<Block>
} {
  let idSeed = params.idSeed
  const antiCollisionSeed = params.idSeed // won't be reassigned – should be used in clipboard blocks that will be created every time we paste
  const blocksInClipboardMap = new Map(
    params.blocks.flatMap((b) => [
      [idToSourceBlockId(b), b],
      [b.id, b],
    ])
  )
  const blocksToCreate: {
    block: Block
    path: string
    isNested: boolean
  }[] = []
  const blocksToCreateMap = createIndexedMap<Block>({
    autoFallbackKeys: ['sourceBlockId'],
  })
  const blocksWithSameHash: Map<string, Block> = new Map()
  const solvedSupportBlocks: Map<string, BlockWithPath> = new Map()

  const resolvedIDs = new Map<string, string>()

  function createOrRetrieveSupportBlock<T extends boolean = true>(
    previousId: string,
    options: {
      required?: T
      allowedTypes?: BlockType[]
      relativePath?: string[]
    } = { required: true as T }
  ): T extends true
    ? AtLeast<BlockWithPath, 'id' | 'sourceBlockId' | 'type'> & {
        previousIdentifiers: { id: string; sourceBlockId?: string | null }
      }
    :
        | (AtLeast<BlockWithPath, 'id' | 'sourceBlockId' | 'type'> & {
            previousIdentifiers: {
              id: string
              sourceBlockId?: string | null
            }
          })
        | null {
    options.required = options.required ?? (true as T)
    let foundBlock =
      blocksWithSameHash.get(previousId) ?? solvedSupportBlocks.get(previousId)
    if (foundBlock) {
      return { ...foundBlock, previousIdentifiers: { id: previousId } }
    }

    const supportBlock = params.supportBlocksData.find(
      (b) => b.sourceBlockId === previousId || b.id === previousId
    )

    if (supportBlock?.sourceBlockId === previousId) {
      foundBlock =
        blocksWithSameHash.get(supportBlock.id) ??
        solvedSupportBlocks.get(supportBlock.id)
    } else if (supportBlock?.id === previousId && supportBlock?.sourceBlockId) {
      foundBlock =
        blocksWithSameHash.get(supportBlock.sourceBlockId) ??
        solvedSupportBlocks.get(supportBlock.sourceBlockId)
    }

    if (foundBlock) {
      return {
        ...foundBlock,
        previousIdentifiers: {
          id: previousId,
          sourceBlockId: supportBlock?.sourceBlockId,
        },
      }
    }

    if (!options.required && !supportBlock) {
      return null as T extends true ? never : null
    }

    if (supportBlock?.type === 'workflow') {
      throw new Error(
        'Workflow blocks should not be used as support blocks when pasting.'
      )
    }

    invariant(
      supportBlock,
      `Block with id ${previousId} not found in support data when pasting block`
    )
    invariant(
      !options.allowedTypes ||
        options.allowedTypes.includes(supportBlock.type satisfies BlockType),
      'Block type not allowed'
    )

    const existingSupportBlock = params.treeManager.getNode(
      idToSourceBlockId(supportBlock)
    )?.value
    if (
      existingSupportBlock &&
      areBlocksCompatible(existingSupportBlock, supportBlock)
    ) {
      // can reuse this one
      blocksWithSameHash.set(previousId, existingSupportBlock)
      return {
        ...existingSupportBlock,
        previousIdentifiers: {
          id: previousId,
          sourceBlockId: supportBlock.sourceBlockId,
        },
      }
    }

    const newId = getIdempotentId({
      idSeed: idToSourceBlockId(supportBlock) + antiCollisionSeed,
    })

    const previouslyCreatedSupportBlock =
      params.treeManager.getNode(newId)?.value
    if (previouslyCreatedSupportBlock) {
      blocksWithSameHash.set(previousId, previouslyCreatedSupportBlock)
      return {
        ...previouslyCreatedSupportBlock,
        previousIdentifiers: {
          id: previousId,
          sourceBlockId: supportBlock.sourceBlockId,
        },
      }
    }

    const newBlock: BlockWithPath = {
      ...cloneDeep(supportBlock),
      sourceBlockId: null, // we don't want to set sourceBlockId here, as it's a new block in the tree
      isCommitted: false,
      apiName: null,
      slug: null,
      id: newId,
      relativePath: options.relativePath ?? [],
    }
    solvedSupportBlocks.set(previousId, newBlock)

    // <previousId, newBlockIds>
    const newChildrenMap: Map<
      string,
      AtLeast<BlockRelationship, 'blockId' | 'sourceBlockId'>
    > = new Map()
    if (isBlockWithChildren(newBlock) && newBlock.relationships) {
      for (const relation of newBlock.relationships) {
        const { id, sourceBlockId, previousIdentifiers } =
          createOrRetrieveSupportBlock(
            relation.sourceBlockId ?? relation.blockId,
            { relativePath: [...newBlock.relativePath, newId] }
          )
        newChildrenMap.set(
          previousIdentifiers?.sourceBlockId ?? relation.blockId,
          { blockId: id, sourceBlockId: sourceBlockId ?? id }
        )
      }
    }

    if ('acceptedVariantSets' in newBlock.constraints) {
      newBlock.constraints.acceptedVariantSets =
        newBlock.constraints.acceptedVariantSets
          ?.map((id) => getPastedBlockSourceBlockId(id))
          .filter(Boolean)
    }

    if ('variantOverrides' in newBlock && newBlock.variantOverrides) {
      newBlock.variantOverrides = Object.fromEntries(
        Object.entries(newBlock.variantOverrides)
          .map(([key, override]) => {
            const formatted = getVariantsDataFromKey(key)
            const newData: VariantData[] = []
            for (const data of formatted) {
              const setId = getPastedBlockSourceBlockId(data.setId)
              if (setId) {
                newData.push({ setId, variantId: data.variantId })
              }
            }
            if (newData.length === 0) {
              return null
            }
            return [getVariantsKey(newData), override]
          })
          .filter(Boolean)
      )
    }

    switch (newBlock.type) {
      case 'variant-set': {
        break
      }
      case 'instance': {
        const newTargetBlockId = getPastedBlockSourceBlockId(
          newBlock.typeOptions.targetBlockId
        )
        if (newTargetBlockId) {
          newBlock.typeOptions.targetBlockId = newTargetBlockId
        }
        break
      }
      case 'rich-text': {
        if (!newBlock.constraints.allowedComponents) break
        let index = 0
        for (const previousId of newBlock.constraints.allowedComponents) {
          const newId = getPastedBlockSourceBlockId(previousId)
          if (newId) {
            newBlock.constraints.allowedComponents[index] = newId
          }
          index++
        }

        const { state: updatedState } = updateRichTextStateBasedOnNewChildren({
          state: newBlock.value.state,
          newChildrenMap,
        })

        newBlock.value = {
          ...newBlock.value,
          state: updatedState.base64,
          transformations: null,
        }

        break
      }
      case 'reference': {
        if (!newBlock.constraints.acceptedTypes?.length) {
          if (newBlock.type === 'reference') {
            newBlock.value.targetBlockIds = []
            newBlock.relationships = []
          }
          break
        }

        const newAcceptedTypes: string[] = []
        for (const id of newBlock.constraints.acceptedTypes) {
          const newId = getPastedBlockSourceBlockId(id)
          if (newId) newAcceptedTypes.push(newId)
        }

        newBlock.constraints.acceptedTypes = newAcceptedTypes

        if (newBlock.type === 'reference') {
          const newTargets: string[] = []
          for (const id of newBlock.value.targetBlockIds) {
            const newId = getPastedBlockSourceBlockId(id)
            if (newId) newTargets.push(newId)
          }
          newBlock.value.targetBlockIds = newTargets
        }
        break
      }
      case 'list': {
        const newTargetBlockId = getPastedBlockSourceBlockId(
          newBlock.typeOptions.targetBlockId
        )
        if (newTargetBlockId) {
          newBlock.typeOptions.targetBlockId = newTargetBlockId
        }

        if (!newBlock.value.views) break
        for (const view of newBlock.value.views) {
          const newView: typeof view = { ...view, columnsMetadata: [] }
          let index = 0
          for (const column of view.columnsMetadata) {
            if (column.blockId.startsWith('__')) {
              newView.columnsMetadata.push(column)
              continue
            }
            const newId = getPastedBlockSourceBlockId(column.blockId)
            if (newId) {
              newView.columnsMetadata.push({ ...column, blockId: newId })
            }
          }
          newBlock.value.views[index] = newView
          index++
        }
        break
      }
    }

    return {
      ...newBlock,
      previousIdentifiers: {
        id: previousId,
        sourceBlockId: supportBlock.sourceBlockId,
      },
    }
  }

  const getPastedBlockSourceBlockId = (
    idOrBlockCopy: string | Block
  ): string | null => {
    let sourceBlockIdCopy =
      typeof idOrBlockCopy === 'string'
        ? idOrBlockCopy
        : idToSourceBlockId(idOrBlockCopy)

    const toCopy = blocksInClipboardMap.get(sourceBlockIdCopy)
    if (toCopy) {
      sourceBlockIdCopy = idToSourceBlockId(toCopy)
    }

    if (resolvedIDs.has(sourceBlockIdCopy)) {
      return resolvedIDs.get(sourceBlockIdCopy)!
    }

    let resolved = ''

    const inTree = params.treeManager.getNode(sourceBlockIdCopy)
    if (!inTree && !blocksInClipboardMap.has(sourceBlockIdCopy)) {
      // it might be a support block
      const supBlock = createOrRetrieveSupportBlock(sourceBlockIdCopy, {
        required: false,
      })
      if (!supBlock) return null
      resolved = idToSourceBlockId(supBlock)
    } else {
      // anti-collision
      if (blocksInClipboardMap.has(sourceBlockIdCopy)) {
        // is copied directly
        resolved = getIdempotentId({
          idSeed: sourceBlockIdCopy + antiCollisionSeed,
        })
      } else {
        // it might be a support block
        const supBlock = createOrRetrieveSupportBlock(sourceBlockIdCopy, {
          required: false,
        })
        if (!supBlock) return null
        resolved = idToSourceBlockId(supBlock)
      }
    }
    resolvedIDs.set(sourceBlockIdCopy, resolved)
    return resolved
  }

  for (const block of params.blocks) {
    const blockCopy = cloneDeep(block) as typeof block
    const debug = false
    const newBlockSourceBlockId = getPastedBlockSourceBlockId(blockCopy)
    const newBlockId = getIdempotentId({
      idSeed: newBlockSourceBlockId + antiCollisionSeed,
    })
    blockCopy.id = newBlockId
    blockCopy.sourceBlockId = newBlockSourceBlockId
    blockCopy.isCommitted = false
    blockCopy.apiName = null
    blockCopy.slug = null

    if (debug) {
      console.log({ newBlockSourceBlockId, newBlockId, blockCopy })
    }

    if (blockCopy.instanceChildTarget) {
      blockCopy.instanceChildTarget = getPastedBlockSourceBlockId(
        blockCopy.instanceChildTarget
      )
    }

    if ('acceptedVariantSets' in blockCopy.constraints) {
      blockCopy.constraints.acceptedVariantSets =
        blockCopy.constraints.acceptedVariantSets
          ?.map((id) => getPastedBlockSourceBlockId(id))
          .filter(Boolean)
    }

    if ('variantOverrides' in blockCopy && blockCopy.variantOverrides) {
      blockCopy.variantOverrides = Object.fromEntries(
        Object.entries(blockCopy.variantOverrides)
          .map(([key, override]) => {
            const formatted = getVariantsDataFromKey(key)
            const newData: VariantData[] = []
            for (const data of formatted) {
              const setId = getPastedBlockSourceBlockId(data.setId)
              if (setId) newData.push({ ...data, setId })
            }
            if (newData.length === 0) {
              return null
            }
            return [getVariantsKey(newData), override]
          })
          .filter(Boolean)
      )
    }

    switch (blockCopy.type) {
      case 'workflow': {
        switch (blockCopy.value.trigger.type) {
          case 'layout-block': {
            if (blockCopy.value.trigger.layoutBlockId) {
              blockCopy.value.trigger.layoutBlockId =
                getPastedBlockSourceBlockId(
                  blockCopy.value.trigger.layoutBlockId
                )
            }

            const newBlockIds: string[] = []
            for (const blockId of blockCopy.value.trigger.blockIds) {
              const newId = getPastedBlockSourceBlockId(blockId)
              if (newId) newBlockIds.push(newId)
            }

            blockCopy.value.trigger.blockIds = newBlockIds
            break
          }
          case 'list-block':
          case 'event-block': {
            const newBlockId = getPastedBlockSourceBlockId(
              blockCopy.value.trigger.blockId
            )
            if (newBlockId) {
              blockCopy.value.trigger.blockId = newBlockId
            }
            break
          }
          case 'commit':
            // no blockId references to handle
            break
        }

        const newActions: typeof blockCopy.value.actions = {}
        for (const [actionKey, action] of Object.entries(
          blockCopy.value.actions
        )) {
          switch (action.type) {
            case 'ai-autofill':
            case 'ai-autofill-variant': {
              const agentId = getPastedBlockSourceBlockId(action.agentId)

              if (agentId) {
                if (action.blockId) {
                  newActions[actionKey] = {
                    ...action,
                    agentId,
                    blockId: getPastedBlockSourceBlockId(action.blockId),
                  }
                } else {
                  newActions[actionKey] = {
                    ...action,
                    agentId,
                  } // blockId is null, keep as is
                }
              }
              break
            }
            default: {
              newActions[actionKey] = action // no blockId or agentId to handle
              break
            }
          }
        }
        blockCopy.value.actions = newActions

        break
      }
      case 'variant-set': {
        blockCopy.value.variants = blockCopy.value.variants
          .map((variant) => {
            const id = getPastedBlockSourceBlockId(variant.id)
            if (!id) return null
            return { ...variant, id }
          })
          .filter(Boolean)
        break
      }
      case 'instance': {
        const newTargetBlockId = getPastedBlockSourceBlockId(
          blockCopy.typeOptions.targetBlockId
        )
        if (newTargetBlockId) {
          blockCopy.typeOptions.targetBlockId = newTargetBlockId
        }

        const isPastingOnList = isListBlock(params.parentBlock)
        if (
          isPastingOnList &&
          blockCopy.relativePath.length === 0 &&
          blockCopy.typeOptions.targetBlockId !==
            (
              params.treeManager.getNode(
                idToSourceBlockId(params.parentBlock),
                { required: true }
              ).value as ListBlock
            ).typeOptions.targetBlockId
        ) {
          throw new MutatorUserError(
            "Cannot paste instances that don't belong to the same template component."
          )
        }
        break
      }
      case 'rich-text': {
        if (!blockCopy.constraints.allowedComponents) break
        let index = 0
        for (const previousId of blockCopy.constraints.allowedComponents) {
          const newId = getPastedBlockSourceBlockId(previousId)
          if (newId) {
            blockCopy.constraints.allowedComponents[index] = newId
          }
          index++
        }

        const newChildrenMap = new Map<
          string,
          AtLeast<BlockRelationship, 'blockId' | 'sourceBlockId'>
        >(
          (
            blockCopy.relationships?.map(
              (
                c
              ):
                | null
                | [
                    string,
                    AtLeast<BlockRelationship, 'blockId' | 'sourceBlockId'>,
                  ] => {
                const child = blocksInClipboardMap.get(
                  c.sourceBlockId ?? c.blockId
                )
                invariant(child, `child ${c.blockId} not found`)
                const previousId = idToSourceBlockId(child)
                const newId = getPastedBlockSourceBlockId(previousId)
                if (!newId) return null
                return [previousId, { blockId: newId, sourceBlockId: newId }]
              }
            ) ?? []
          ).filter(Boolean)
        )

        const { state: updatedState } = updateRichTextStateBasedOnNewChildren({
          state: blockCopy.value.state,
          newChildrenMap,
        })

        blockCopy.value = {
          ...blockCopy.value,
          state: updatedState.base64,
          transformations: null,
        }

        break
      }
      case 'reference': {
        if (!blockCopy.constraints.acceptedTypes?.length) {
          if (blockCopy.type === 'reference') {
            blockCopy.value.targetBlockIds = []
            blockCopy.relationships = []
          }
          break
        }

        const newAcceptedTypes: string[] = []
        for (const id of blockCopy.constraints.acceptedTypes) {
          const newId = getPastedBlockSourceBlockId(id)
          if (newId) newAcceptedTypes.push(newId)
        }

        blockCopy.constraints.acceptedTypes = newAcceptedTypes

        if (blockCopy.type === 'reference') {
          const newTargets: string[] = []
          for (const id of blockCopy.value.targetBlockIds) {
            const newId = getPastedBlockSourceBlockId(id)
            if (newId) newTargets.push(newId)
          }
          blockCopy.value.targetBlockIds = newTargets
        }
        break
      }
      case 'list': {
        const newTargetBlockId = getPastedBlockSourceBlockId(
          blockCopy.typeOptions.targetBlockId
        )
        if (newTargetBlockId) {
          blockCopy.typeOptions.targetBlockId = newTargetBlockId
        }

        if (!blockCopy.value.views) break
        for (const view of blockCopy.value.views) {
          const newView: typeof view = { ...view, columnsMetadata: [] }
          let index = 0
          for (const column of view.columnsMetadata) {
            if (column.blockId.startsWith('__')) {
              newView.columnsMetadata.push(column)
              continue
            }
            const newId = getPastedBlockSourceBlockId(column.blockId)
            if (newId) {
              newView.columnsMetadata.push({
                ...column,
                blockId: newId,
              })
            }
          }
          blockCopy.value.views[index] = newView
          index++
        }
        break
      }
    }

    const resolvedParentsIds = block.relativePath
      .map((previousId) => {
        const newBlockSourceBlockId = getPastedBlockSourceBlockId(previousId)
        if (!newBlockSourceBlockId) return null
        const existing =
          params.treeManager.getNode(newBlockSourceBlockId)?.value ??
          blocksToCreateMap.get(newBlockSourceBlockId)
        if (debug) {
          console.log({
            newBlockSourceBlockId,
            previousId,
            existing,
            fallback: getIdempotentId({
              idSeed: newBlockSourceBlockId + antiCollisionSeed,
            }),
          })
        }
        if (existing) return existing.id
        return getIdempotentId({
          idSeed: newBlockSourceBlockId + antiCollisionSeed,
        })
      })
      .filter(Boolean)

    if (debug) {
      console.log({
        blockCopy,
        ogParentIds: block.relativePath,
        resolvedParentsIds,
        resolved: normalizeStringPath({
          path: params.path,
          edgeId: resolvedParentsIds.join('/'),
          format: 'string',
        }),
      })
    }

    blocksToCreate.push({
      block: blockCopy,
      path: normalizeStringPath({
        path: params.path,
        edgeId: resolvedParentsIds.join('/'),
        format: 'string',
      }),
      isNested: resolvedParentsIds.length > 0,
    })
    blocksToCreateMap.set(newBlockId, blockCopy)
  }

  const instancesGroupedByTarget = new Map<string, string[]>()
  let supportBlocksNeedToBeWrapped = false
  let firstRootBlockFound = false

  const supportBlocksByNewId = new Map<string, BlockWithPath>(
    Array.from(solvedSupportBlocks.values()).map((block) => [
      idToSourceBlockId(block),
      block,
    ])
  )

  for (const block of solvedSupportBlocks.values()) {
    if (!block.relativePath.length) {
      if (firstRootBlockFound) {
        supportBlocksNeedToBeWrapped = true
      }
      firstRootBlockFound = true
    }

    if (isInstanceBlock(block) && !block.relativePath.length) {
      const found = instancesGroupedByTarget.get(
        block.typeOptions.targetBlockId
      )
      instancesGroupedByTarget.set(
        block.typeOptions.targetBlockId,
        found
          ? [...found, idToSourceBlockId(block)]
          : [idToSourceBlockId(block)]
      )
    }

    if (isComponentBlock(block) && !block.relativePath.length) {
      const found = instancesGroupedByTarget.get(idToSourceBlockId(block))
      instancesGroupedByTarget.set(
        idToSourceBlockId(block),
        found
          ? [...found, idToSourceBlockId(block)]
          : [idToSourceBlockId(block)]
      )
    }
  }

  function addListToChildrenPaths(
    block: BlockWithPath & BlockWithChildren,
    listId: string
  ) {
    for (const { blockId } of block.relationships ?? []) {
      const newId = getPastedBlockSourceBlockId(blockId)
      if (!newId) continue
      const child = supportBlocksByNewId.get(newId)
      invariant(child, 'child not found')
      child.relativePath.unshift(listId)

      if (isBlockWithChildren(child) && child.relationships) {
        addListToChildrenPaths(child, listId)
      }
    }
  }

  for (const [key, ids] of instancesGroupedByTarget.entries()) {
    if (ids.length > 1) {
      const group = ids
        .map((id) => supportBlocksByNewId.get(id))
        .filter(Boolean)
      const componentTitle = group.find((i) => i.type === 'component')?.title
      const listId = (idSeed = getIdempotentId({
        idSeed,
        splitPaths: { newPath: 'copy/support-collection' },
      }))
      const newList = {
        id: listId,
        title: 'Support collection for ' + (componentTitle || key),
        type: 'list',
        typeOptions: {
          targetBlockId: key,
        },
        value: {
          views: [],
        },
        relativePath: [],
      } satisfies AtLeast<
        BlockWithPath,
        'id' | 'type' | 'relativePath'
      > as unknown as BlockWithPath

      supportBlocksByNewId.set(listId, newList)

      for (const block of group) {
        block.relativePath.unshift(newList.id)
        supportBlocksByNewId.set(block.id, block)

        if (isBlockWithChildren(block)) {
          addListToChildrenPaths(block, listId)
        }
      }
    }
  }

  const supportBlocks = Array.from(supportBlocksByNewId.values()).sort(
    (a, b) => {
      const delta = a.relativePath.length - b.relativePath.length
      if (delta !== 0) return delta
      if (b.type === 'component') return 1
      return -1
    }
  )

  if (supportBlocksNeedToBeWrapped) {
    const newId = (idSeed = getIdempotentId({
      idSeed,
      splitPaths: { newPath: 'copy/support-blocks-wrapper' },
    }))
    const newBlock = {
      id: newId,
      type: 'document',
      value: {},
      title: 'Support Blocks Wrapper',
    } as Block

    const pathToSupportBlocks = !isDocumentBlock(params.parentBlock)
      ? params.treeManager.root.key
      : params.path

    blocksToCreate.unshift(
      ...supportBlocks.map((b) => {
        blocksToCreateMap.set(b.id, b)
        return {
          block: b,
          path: normalizeStringPath({
            path: pathToSupportBlocks,
            edgeId: [newId, ...b.relativePath].join('/'),
            format: 'string',
          }),
          isNested: true,
        }
      })
    )
    blocksToCreate.unshift({
      block: newBlock,
      path: pathToSupportBlocks,
      isNested: false,
    })
    blocksToCreateMap.set(newBlock.id, newBlock)

    return { blocksToCreate, blocksToCreateMap }
  }

  return {
    blocksToCreateMap,
    blocksToCreate: [
      ...supportBlocks.map((b) => {
        blocksToCreateMap.set(b.id, b)
        return {
          block: b,
          path: normalizeStringPath({
            path: params.path,
            edgeId: b.relativePath.join('/'),
            format: 'string',
          }),
          isNested: b.relativePath.length > 0,
        }
      }),
      ...blocksToCreate,
    ],
  }
}

export const pasteBlock = async (
  tx: WriteTransaction,
  params: WithServerSecret<PasteBlockParams>
) => {
  ensureBaseHubTransaction(tx)

  const undo: BlockBatchMutationParams['mutations'] = []

  if (params.type === 'copy' && params.pastedBlocksData.length === 1) {
    if (params.selectedBlock) {
      const pastedBlockData = params.pastedBlocksData[0]

      const { block: selectedBlock, parentBlock: selectedBlockParent } =
        await doCopyOnWrite(tx, {
          partialBlock: {
            id: params.selectedBlock.id,
            instanceChildTarget: params.selectedBlock.instanceChildTarget,
          },
          isoNow: params.isoNow,
          path: params.selectedBlock.path,
        })

      if (pastedBlockData.type === selectedBlock.type) {
        let shouldJustReplaceValue = false
        let valueToPaste = pastedBlockData.value
        switch (selectedBlock.type) {
          case 'media':
            invariant(
              isMediaBlock(pastedBlockData),
              'Block is not a media block'
            )
            if (pastedBlockData.value.status === 'uploaded') {
              shouldJustReplaceValue = true
            }
            break
          case 'event':
            invariant(
              isEventBlock(pastedBlockData),
              'Block is not an event block'
            )
            valueToPaste = {
              schema:
                pastedBlockData.value.schema ??
                pastedBlockData.constraints.schema,
            } satisfies EventBlock['value']
            break
          case 'rich-text':
            invariant(
              isRichTextBlock(pastedBlockData),
              'Block is not a rich text block'
            )
            valueToPaste = {
              ...pastedBlockData.value,
              lastRevertedAt: params.isoNow,
            } satisfies RichTextBlock['value']
            break

          default:
            break
        }

        /** Instances can't have new children, so we assume the user wants to replace the value. */
        if (selectedBlockParent.type === 'instance') {
          shouldJustReplaceValue = true
        }
        if (
          idToSourceBlockId(selectedBlock) ===
            idToSourceBlockId(pastedBlockData) &&
          selectedBlock.id !== pastedBlockData.id
        ) {
          // same source, different trees. ux is: "i got a block from, say, another commit, and i wanna roll it back"
          shouldJustReplaceValue = true
        }
        if (params.shiftKeyPressed) {
          shouldJustReplaceValue = true
        }

        if (shouldJustReplaceValue) {
          // interesanchi... what we'll do here is just update the image block with the new image, instead of duplicating it.
          const { undo: undoReplaceValue } = await updateBlock(tx, {
            id: params.selectedBlock.id,
            data: {
              type: pastedBlockData.type,
              value: valueToPaste,
              ...(selectedBlockParent.type !== 'instance'
                ? { constraints: pastedBlockData.constraints }
                : {}),
            } as AtLeast<Block, 'type'>,
            instanceChildTarget: selectedBlock.instanceChildTarget,
            path: params.selectedBlock.path,
            isoNow: params.isoNow,
            strategy: 'merge',
          })
          undo.push(...undoReplaceValue)
          // and we return early to stop the other stuff...
          return { undo }
        }
      }
    }
  }

  if (params.replaceValueOnly) {
    throw new MutatorUserError(
      'Unlock schema editing in order to paste blocks.'
    )
  }

  let newBlock
  const idSeed = params.idSeed
  if (params.type === 'copy') {
    const supportBlocksData = params.supportBlocksData
    const treeManager = await tx.getTreeManager(params.rootId)
    const parentBlock = treeManager.getNode(
      params.path.split('/').at(-1) as string,
      { required: true }
    ).value

    if (
      editorCanEditBlocks({
        parentBlock: parentBlock,
        blocks: params.pastedBlocksData,
      })
    ) {
      // if it's a list child, we ensure the user is at least editor
      hasAccessOrThrow({
        serverOnly: tx.serverOnly,
        clientOnly: tx.clientOnly,
        serverSecret: tx.serverSecret,
        allowedRoles: ['editor', 'owner', 'member'],
        guestAccess: 'branch-author',
      })
    } else {
      // ensure user is owner or member
      hasAccessOrThrow({
        serverOnly: tx.serverOnly,
        clientOnly: tx.clientOnly,
        serverSecret: tx.serverSecret,
        allowedRoles: ['owner', 'member'],
        guestAccess: 'branch-author',
      })
    }

    const { blocksToCreate } = saveBlocksToCreate(tx, {
      blocks: params.pastedBlocksData,
      idSeed,
      treeManager,
      supportBlocksData,
      path: params.path,
      repoId: params.repoId,
      parentBlock,
    })

    const blocksThatNeedValidation: Set<{
      block: Block
      path: string
    }> = new Set()

    for (const { block, path, isNested } of blocksToCreate) {
      if (!isNested) {
        if (isListBlock(parentBlock) && !isInstanceBlock(block)) {
          throw new MutatorUserError(
            'Cannot paste blocks inside a list. Paste them inside a document or component instead.'
          )
        }
        await validateBlockNestingPromise({
          block,
          fetchBlock: (id) => blockBaseOps.get(tx, id),
          fetchChildren: async (sourceBlockId) => {
            const brs = await tx.getBlockChildren({
              sourceBlockId,
              rootBlockId: params.rootId,
            })

            return getBlocks(
              tx,
              brs.map((br) => br.blockId)
            )
          },
          shouldThrowIfInvalid: true,
          path,
        })
      }

      const parentId = normalizeStringPath({
        path,
        edgeId: block.id,
        includeEdge: false,
      }).pop()
      const parent = parentId
        ? (treeManager.getNode(parentId)?.value ??
          blocksToCreate.find(
            (b) => b.block.id === parentId || b.block.sourceBlockId === parentId
          )?.block)
        : null

      const {
        newBlock: result,
        undo: undoCreate,
        parentBlock: newParent,
        newBlockRelationship,
      } = await createBlock(tx, {
        data: block,
        path,
        isoNow: params.isoNow,
        // We insert nested blocks one after the other with 'last' position
        position:
          path === params.path && params.position
            ? params.position
            : { type: 'last' },
        idSeed: getIdempotentId({ idSeed: idSeed + block.id }),
        skipInstanceValidation: true,
        skipUpdatingParent: true,
        skipPropagateHiddenState: isNested,
        parentInstanceChildTarget: parent?.instanceChildTarget ?? null,
        prefetchedParentBlock: parent ?? undefined,
      })
      undo.unshift(...undoCreate)

      if (newBlockRelationship && result) {
        treeManager.addNode(result, {
          parentKey: newParent.id,
          order: newBlockRelationship.order,
        })
      }

      // We return one of the first level blocks
      if (!isNested && path.startsWith(params.path)) {
        newBlock = result
      }

      if (
        !isNested &&
        (isInstanceBlock(block) ||
          isDocumentBlock(block) ||
          isComponentBlock(block))
      ) {
        blocksThatNeedValidation.add({
          block,
          path,
        })
      }
    }

    for (const args of blocksThatNeedValidation) {
      const result = await validateBlockNestingPromise({
        ...args,
        fetchBlock: (id) => blockBaseOps.get(tx, id),
        fetchChildren: async (sourceBlockId) => {
          const brs = await tx.getBlockChildren({
            sourceBlockId,
            rootBlockId: params.rootId,
          })

          return getBlocks(
            tx,
            brs.map((br) => br.blockId)
          )
        },
        shouldThrowIfInvalid: false,
      })

      if (!result.isValid) throw new MutatorUserError(result.reason)
    }
  } else {
    const reorderResult = await reorderBlocks(tx, { ...params })
    if (reorderResult?.undo) {
      undo.push(...reorderResult.undo)
    }
  }

  if (params.replaceBlockId) {
    const deleteResult = await deleteBlock(tx, {
      id: params.replaceBlockId,
      path: params.path,
      isoNow: new Date().toISOString(),
      idSeed: getIdempotentId({
        idSeed,
        splitPaths: { newPath: 'paste-action-remove-block' },
      }),
    })
    if (deleteResult?.undo) {
      undo.push(...deleteResult.undo)
    }
  }

  return { newBlock, undo }
}
